import {isNullOrEmpty, UserError} from "@2d-front-monorepo/core";
import {CreditCardProvider} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {useEffect, useRef} from "react";
import {API_ENDPOINT} from "../api-endpoint";
import {formatRoute} from "../routes";
import {usePermits} from "./usePermits";

type ChannelData = {
	from: string;
	externalId: string;
	error?: string | undefined;
	requestId?: string | undefined;
	traceId?: string | undefined;
}

class CreditCardAssignmentEngine {
	private readonly _broadcastChannel: BroadcastChannel;
	private readonly _cardProvider: CreditCardProvider;
	private _checkClosed: number;
	private readonly _externalId: string;
	private _openedWindow: Window | null | undefined;
	private readonly _plates: string[];
	private _reject: ((reason?: any) => void) | undefined;
	private _resolve: ((value: (PromiseLike<string> | string)) => void) | undefined;

	constructor(cardProvider: CreditCardProvider, externalId: string, plates: string[]) {
		this._cardProvider = cardProvider;
		this._externalId = externalId;
		this._plates = plates;
		this._broadcastChannel = this.registerBroadcastChannel();
		this._checkClosed = -1;
	}

	public dispose(): void {
		this._broadcastChannel.close();
	}

	public startRegister(): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			this._resolve = resolve;
			this._reject = reject;
			const plates = this._plates.join(",");
			this._openedWindow = window.open(formatRoute(API_ENDPOINT.registerPayment, this._cardProvider, `${location.origin}/payment.html`, this._externalId, plates), "_blank");
			if (this._openedWindow != null) {
				this._checkClosed = setInterval(() => {
					if (this._openedWindow && this._openedWindow.closed) {
						clearInterval(this._checkClosed);
						reject(new UserError(
							{text: PermitTranslations.payments_page__close_window__title},
							{text: PermitTranslations.payments_page__close_window__description},
							[],
							{text: PermitTranslations.payments_page__close_window__footer})
						);
					}
				}, 250);

			} else {
				reject(new UserError(
					{text: PermitTranslations.payments_page__popup_error__title},
					{text: PermitTranslations.payments_page__popup_error__description},
					[],
					{text: PermitTranslations.payments_page__popup_error__footer})
				);
			}
		});
	}

	private registerBroadcastChannel(): BroadcastChannel {
		const broadcastChannel = new BroadcastChannel("permits_channel");
		broadcastChannel.onmessage = (event: MessageEvent) => {
			const {error, externalId, requestId, traceId} = event.data as ChannelData;
			clearInterval(this._checkClosed);
			this._openedWindow?.close();
			if (isNullOrEmpty(error)) {
				if (this._resolve) {
					this._resolve(externalId);
				}

			} else {
				if (!this._reject) {
					return;
				}

				this._reject(new UserError(
					{text: PermitTranslations.payments_page__authorization_error__title},
					{text: PermitTranslations.payments_page__authorization_error__description},
					[
						{text: error},
						{text: "ꭲꮖꭰ: {value}", args: {"value": traceId}},
						{text: "ꭱꮖꭰ: {value}", args: {"value": requestId}}
					],
					{text: PermitTranslations.payments_page__authorization_error__footer}
				));
			}
		}

		return broadcastChannel;
	}
}

export function useCreditCardAssignment(externalId: string, plates: string[]): [CreditCardProvider, () => Promise<string>] {
	const {holderData} = usePermits();
	const defaultCardProvider = holderData.paymentProviders?.providers[0].id ?? CreditCardProvider.Undefined;
	const currentCardProvider = holderData.paymentProviders?.creditCardProvider ?? CreditCardProvider.Undefined;
	const engine = useRef<CreditCardAssignmentEngine>(null);

	useEffect(() => {
		engine.current = new CreditCardAssignmentEngine(defaultCardProvider, externalId, plates);
		return () => {
			engine.current?.dispose();
		}
	}, [defaultCardProvider, externalId]);

	return [currentCardProvider, () => {
		return engine.current?.startRegister() ?? Promise.reject("engine_not_assigned")
	}];
}
